import { ChannelRepository, MessageContentType, MessageRepository, UserRepository, Client, API_REGIONS } from "@amityco/ts-sdk";
import useMembers, { fetchMembersFromAmity } from "../hooks/useMembers";

export const UPTIME_BOT_ID = 'uptime-bot';

export interface IChannel {
    id: string;
    displayName: string;
    lastActivity: Date;
    hasUnreadMessage: boolean;
    members: IMember[];
    profileImage?: string;
    group?: boolean;
}

export interface IMember {
    userId: string;
    profileUniqueId: string;
    displayName: string;
    profileImage?: string;
    rioUserIds: string[];
}

export const getAmityToken = (): string | null => {
    try {
        const storedToken = sessionStorage.getItem('amity_access_token');
        if (storedToken) {
            return storedToken;
        }
        
        return null;
    } catch (error) {
        console.error('Error getting Amity token:', error);
        return null;
    }
};

export const createChannel = async (members: IMember[], ownerId: string): Promise<IChannel> => {
    const createdChannel = await ChannelRepository.createChannel(
        {
            displayName: `${ownerId}-len-${members.length + 1}`,
            type: 'community',
            userIds: [...members.map((member) => member.userId), ownerId],
            metadata: {
                deletedUsers: [],
                group: false,
                users: [...members.map((member) => member.userId), ownerId],
            },
            tags: ["tag"],
        }
    );

    return {
        id: createdChannel.data._id,
        displayName: members.map((member) => member.displayName).join(', '),
        lastActivity: new Date(createdChannel.data.lastActivity ?? ''),
        hasUnreadMessage: false,
        members,
    };
}

export const getMembers = (setMembers: (members: IMember[]) => void): void => {
    const state = useMembers.getState();
    
    if (state.allMembers.length > 0) {
        setMembers(state.allMembers);
        return;
    }

    fetchMembersFromAmity((members: IMember[]) => {
        useMembers.setState({ allMembers: members });
        setMembers(members);
    });
}

export const createTextMessage = async (newMessage: string, channelId: string) => {
    const textMessage = {
        subChannelId: channelId,
        dataType: MessageContentType.TEXT,
        data: {
            text: newMessage,
        }
    };

    const { data: message } = await MessageRepository.createMessage(textMessage);

    return message;
}

export const createMessageWithLink = async (message: string, linkUrl: string, linkText: string, channelId: string) => {
    const messageWithLink = `${message}\n\n${linkText}: ${linkUrl}`;
    
    const textMessage = {
        subChannelId: channelId,
        dataType: MessageContentType.TEXT,
        data: {
            text: messageWithLink,
        }
    };

    const { data: messageData } = await MessageRepository.createMessage(textMessage);

    return messageData;
}

export const markAllMessageAsRead = async (channelId: string) => {
    await ChannelRepository.markAsRead(channelId);
}

export const updateUserName = async (userId: string, givenName?: string, familyName?: string, accountId?: string) => {
    try {
        UserRepository.getUser(userId, async response => {
            const user = response.data;
            const metadata = user?.metadata || {};
            if (metadata.givenName === givenName && metadata.familyName === familyName && metadata.rioId === accountId) return;
            if (!givenName || !familyName || !accountId || userId === 'ec6e5df5-b523-4428-bacd-48d04b90a234') return;
            await UserRepository.updateUser(
                userId,
                {
                    metadata: {
                        ...metadata,
                        givenName,
                        familyName,
                        rioId: accountId,
                        accountId,
                        displayName: '',
                    }
                }
            );
        });
    } catch (error) {
        console.error('Erro ao atualizar metadata:', error);
    }
};

export const sendMessageToUser = async (
    message: string,
    targetUserId: string,
    senderUserId: string = UPTIME_BOT_ID
) => {
    let unsubscriber: (() => void) | null = null;

    try {
        let activeClient;
        try {
            activeClient = Client.getActiveClient();
        } catch (error) {
            Client.createClient(import.meta.env.VITE_AMITY_API_KEY, API_REGIONS.US);
            activeClient = Client.getActiveClient();
        }

        const sessionHandler: Amity.SessionHandler = {
            sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
                renewal.renew();
            },
        };

        await Client.login(
            { userId: senderUserId, displayName: senderUserId },
            sessionHandler,
        );

        let textMessage = {
            subChannelId: '',
            dataType: MessageContentType.TEXT,
            data: {
                text: message,
            },
        };

        unsubscriber = ChannelRepository.getChannels(
            { membership: 'member', limit: 100 },
            async (channelsData) => {
                if(channelsData.loading) return;

                const existingChannel = channelsData.data?.find((channel: any) =>
                    channel.type === 'community' &&
                    channel.metadata?.users?.includes(targetUserId) &&
                    channel.metadata?.users?.includes(senderUserId) &&
                    channel.metadata?.users?.length === 2
                );
                const channelId = existingChannel?._id;
                if (channelId) {
                    textMessage.subChannelId = channelId;
                    MessageRepository.createMessage(textMessage);
                } else {
                    const channelResponse = await ChannelRepository.createChannel(
                        {
                            displayName: `Chat with ${targetUserId}`,
                            type: 'community',
                            userIds: [senderUserId, targetUserId],
                            metadata: {
                                deletedUsers: [],
                                group: false,
                                users: [senderUserId, targetUserId],
                            },
                            tags: ["private"],
                        }
                    );
                    textMessage.subChannelId = channelResponse.data._id;
                    MessageRepository.createMessage(textMessage);
                }
            }
        );

        return unsubscriber();

    } catch (error) {
        console.error('Error sending message to user:', error);
    }
};

export const sendMessageToUsers = async (
    message: string,
    targetUserIds: string[],
    senderUserId: string = UPTIME_BOT_ID
): Promise<Array<{ userId: string; success: boolean; error?: string }>> => {
    if (targetUserIds.length === 0) {
        return [];
    }

    try {
        let activeClient;
        try {
            activeClient = Client.getActiveClient();
        } catch (error) {
            Client.createClient(import.meta.env.VITE_AMITY_API_KEY, API_REGIONS.US);
            activeClient = Client.getActiveClient();
        }

        const sessionHandler: Amity.SessionHandler = {
            sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
                renewal.renew();
            },
        };

        await Client.login(
            { userId: senderUserId, displayName: senderUserId },
            sessionHandler,
        );

        const sendToSingleUser = async (targetUserId: string, messageText: string): Promise<{ userId: string; success: boolean; error?: string }> => {
            try {
                return new Promise((resolve) => {
                    let unsubscriber: (() => void) | null = null;
                    let resolved = false;
                    let messageSent = false;

                    const resolveOnce = (result: { userId: string; success: boolean; error?: string }) => {
                        if (!resolved) {
                            resolved = true;
                            if (unsubscriber) {
                                try {
                                    unsubscriber();
                                } catch (error) {
                                    // Ignore unsubscribe errors
                                }
                            }
                            resolve(result);
                        }
                    };

                    const timeoutId = setTimeout(() => {
                        resolveOnce({ userId: targetUserId, success: false, error: 'Timeout ao buscar canal' });
                    }, 10000);

                    unsubscriber = ChannelRepository.getChannels(
                        { membership: 'member', limit: 100 },
                        async (channelsData) => {
                            if (channelsData.loading) return;
                            if (messageSent) return;

                            clearTimeout(timeoutId);
                            messageSent = true;

                            try {
                                const existingChannel = channelsData.data?.find((channel: any) =>
                                    channel.type === 'community' &&
                                    channel.metadata?.users?.includes(targetUserId) &&
                                    channel.metadata?.users?.includes(senderUserId) &&
                                    channel.metadata?.users?.length === 2
                                );

                                let channelId: string;
                                
                                if (existingChannel?._id) {
                                    channelId = existingChannel._id;
                                } else {
                                    const channelResponse = await ChannelRepository.createChannel(
                                        {
                                            displayName: `Chat with ${targetUserId}`,
                                            type: 'community',
                                            userIds: [senderUserId, targetUserId],
                                            metadata: {
                                                deletedUsers: [],
                                                group: false,
                                                users: [senderUserId, targetUserId],
                                            },
                                            tags: ["private"],
                                        }
                                    );
                                    channelId = channelResponse.data._id;
                                }

                                const textMessage = {
                                    subChannelId: channelId,
                                    dataType: MessageContentType.TEXT,
                                    data: {
                                        text: messageText,
                                    },
                                };

                                await MessageRepository.createMessage(textMessage);
                                resolveOnce({ userId: targetUserId, success: true });
                            } catch (error: any) {
                                resolveOnce({ 
                                    userId: targetUserId, 
                                    success: false, 
                                    error: error?.message || 'Erro ao enviar mensagem' 
                                });
                            }
                        }
                    );
                });
            } catch (error: any) {
                return { 
                    userId: targetUserId, 
                    success: false, 
                    error: error?.message || 'Erro ao processar envio' 
                };
            }
        };

        const results = [];
        for (const userId of targetUserIds) {
            const result = await sendToSingleUser(userId, message);
            results.push(result);
        }

        return results;
    } catch (error: any) {
        console.error('Error sending messages to users:', error);
        return targetUserIds.map(userId => ({
            userId,
            success: false,
            error: error?.message || 'Erro ao inicializar cliente'
        }));
    }
};

export const sendBotMessage = async (
    message: string,
    targetUserId: string
) => {
    return sendMessageToUser(message, targetUserId, UPTIME_BOT_ID);
};

interface IBroadcastMessage {
    message: string;
    targetUserIds: string[];
    link?: string;
    linkLabel?: string;
}

export const sendBotBroadcastMessage = async (
    messages: IBroadcastMessage[]
): Promise<Array<{ userId: string; success: boolean; error?: string }>> => {
    if (messages.length === 0) {
        return [];
    }

    try {
        let activeClient;
        try {
            activeClient = Client.getActiveClient();
        } catch (error) {
            Client.createClient(import.meta.env.VITE_AMITY_API_KEY, API_REGIONS.US);
            activeClient = Client.getActiveClient();
        }

        const sessionHandler: Amity.SessionHandler = {
            sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
                renewal.renew();
            },
        };

        await Client.login(
            { userId: UPTIME_BOT_ID, displayName: UPTIME_BOT_ID },
            sessionHandler,
        );

        const sendToSingleUser = async (targetUserId: string, messageText: string): Promise<{ userId: string; success: boolean; error?: string }> => {
            try {
                return new Promise((resolve) => {
                    let unsubscriber: (() => void) | null = null;
                    let resolved = false;
                    let messageSent = false;

                    const resolveOnce = (result: { userId: string; success: boolean; error?: string }) => {
                        if (!resolved) {
                            resolved = true;
                            if (unsubscriber) {
                                try {
                                    unsubscriber();
                                } catch (error) {
                                    // Ignore unsubscribe errors
                                }
                            }
                            resolve(result);
                        }
                    };

                    const timeoutId = setTimeout(() => {
                        resolveOnce({ userId: targetUserId, success: false, error: 'Timeout ao buscar canal' });
                    }, 10000);

                    unsubscriber = ChannelRepository.getChannels(
                        { membership: 'member', limit: 100 },
                        async (channelsData) => {
                            if (channelsData.loading) return;
                            if (messageSent) return;

                            clearTimeout(timeoutId);
                            messageSent = true;

                            try {
                                const existingChannel = channelsData.data?.find((channel: any) =>
                                    channel.type === 'community' &&
                                    channel.metadata?.users?.includes(targetUserId) &&
                                    channel.metadata?.users?.includes(UPTIME_BOT_ID) &&
                                    channel.metadata?.users?.length === 2
                                );

                                let channelId: string;
                                
                                if (existingChannel?._id) {
                                    channelId = existingChannel._id;
                                } else {
                                    const channelResponse = await ChannelRepository.createChannel(
                                        {
                                            displayName: `Chat with ${targetUserId}`,
                                            type: 'community',
                                            userIds: [UPTIME_BOT_ID, targetUserId],
                                            metadata: {
                                                deletedUsers: [],
                                                group: false,
                                                users: [UPTIME_BOT_ID, targetUserId],
                                            },
                                            tags: ["private"],
                                        }
                                    );
                                    channelId = channelResponse.data._id;
                                }

                                const textMessage = {
                                    subChannelId: channelId,
                                    dataType: MessageContentType.TEXT,
                                    data: {
                                        text: messageText,
                                    },
                                };

                                try {
                                    await MessageRepository.createMessage(textMessage);
                                    resolveOnce({ userId: targetUserId, success: true });
                                } catch (linkError: any) {
                                    if (linkError?.code === 400309 || linkError?.message?.includes('whitelist')) {
                                        const messageWithoutLink = messageText.split('\n\n')[0];
                                        const fallbackMessage = {
                                            subChannelId: channelId,
                                            dataType: MessageContentType.TEXT,
                                            data: {
                                                text: messageWithoutLink,
                                            },
                                        };
                                        await MessageRepository.createMessage(fallbackMessage);
                                        resolveOnce({ userId: targetUserId, success: true });
                                    } else {
                                        throw linkError;
                                    }
                                }
                            } catch (error: any) {
                                resolveOnce({ 
                                    userId: targetUserId, 
                                    success: false, 
                                    error: error?.message || 'Erro ao enviar mensagem' 
                                });
                            }
                        }
                    );
                });
            } catch (error: any) {
                return { 
                    userId: targetUserId, 
                    success: false, 
                    error: error?.message || 'Erro ao processar envio' 
                };
            }
        };

        const results: Array<{ userId: string; success: boolean; error?: string }> = [];
        
        for (const { message, targetUserIds, link, linkLabel } of messages) {
            let finalMessage = message;
            
            if (link && linkLabel) {
                finalMessage = `${message}\n\n${linkLabel}\n${link}`;
            }
            
            for (const userId of targetUserIds) {
                const result = await sendToSingleUser(userId, finalMessage);
                results.push(result);
            }
        }

        return results;
    } catch (error: any) {
        console.error('Error sending broadcast messages:', error);
        const allUserIds = messages.flatMap(msg => msg.targetUserIds);
        return allUserIds.map(userId => ({
            userId,
            success: false,
            error: error?.message || 'Erro ao inicializar cliente'
        }));
    }
};

